// Gedeelde voorwaarden voor stille films over een foto (Dennis 26-10-2026):
// HeroFilm (homepage) en de dealfilms (dealkaart en dealpagina) gebruiken
// dezelfde host-allowlist, dezelfde omgevingsregels en dezelfde proefgroep-
// mechaniek. De foto blijft altijd het LCP-element; een film komt pas na
// `load` + idle en alleen op favotrip.nl.

export const FILM_HOST = "library.favotrip.nl";
export const POSTER_HOSTS = [FILM_HOST];
export const FILM_SITE = "favotrip.nl";
export const FILM_KANS = 0.9;
export const HERO_PROEF_KEY = "ft_hero_proef";
// "Beweging uit" op een herofilm (homepage of dealpagina) zet dit voor alle films,
// alleen voor dit bezoek (sessionStorage). Een oude localStorage-waarde telt niet meer.
export const FILM_UIT_KEY = "ft_hero_film_uit";
export const DESKTOP_QUERY = "(min-width: 768px)";
export const FETCH_TIMEOUT_MS = 1500;
export const IDLE_FALLBACK_MS = 1500;

/** "desktop" vanaf 768px, anders "mobiel". */
export function kiesVariant(matchMediaFn) {
  try {
    return matchMediaFn && matchMediaFn(DESKTOP_QUERY).matches ? "desktop" : "mobiel";
  } catch {
    return "mobiel";
  }
}

/** Genormaliseerde https-URL als de host in `hosts` staat (geen poort of inlog), anders null. */
export function eigenUrl(raw, hosts) {
  if (typeof raw !== "string") return null;
  try {
    const u = new URL(raw);
    if (u.protocol !== "https:" || u.username || u.password || u.port) return null;
    return hosts.includes(u.hostname) ? u.href : null;
  } catch {
    return null;
  }
}

/** Mag er een film komen? Alleen op favotrip.nl zelf (niet favotrip.be, ook al is dat locale nl), geen reduced-motion, geen data-besparing, geen trage lijn, niet eerder uitgezet. */
export function magFilm({ domain, reducedMotion, saveData, effectiveType, uitgezet }) {
  if (domain !== FILM_SITE) return false;
  if (reducedMotion || saveData || uitgezet) return false;
  if (effectiveType === "slow-2g" || effectiveType === "2g") return false;
  return true;
}

/** Stabiele proefgroep ("film"|"foto") uit storage; nieuwe bezoeker: kans FILM_KANS op film. Zonder bruikbare storage altijd "foto". */
export function proefGroep(storage, random = Math.random, key = HERO_PROEF_KEY) {
  try {
    const bewaard = storage.getItem(key);
    if (bewaard === "film" || bewaard === "foto") return bewaard;
    const groep = random() < FILM_KANS ? "film" : "foto";
    storage.setItem(key, groep);
    return groep;
  } catch {
    return "foto";
  }
}

/**
 * Proefgroep van de homepage-herofilm: sinds 28-10-2026 krijgt elke bezoeker de film
 * ("Hero = film"). Een eerder bewaarde "foto" telt niet meer en wordt overschreven.
 * De dealfilms houden hun eigen proefgroep (proefGroep + FILM_KANS).
 */
export function heroGroep(storage, key = HERO_PROEF_KEY) {
  try {
    if (storage) storage.setItem(key, "film");
  } catch {
    // storage onbruikbaar: de film krijgt hij toch
  }
  return "film";
}

export function getStorage() {
  try {
    return window.localStorage || null;
  } catch {
    return null;
  }
}

/** sessionStorage voor de "Beweging uit"-voorkeur (geldt alleen dit bezoek), of null. */
export function getSessieStorage() {
  try {
    return window.sessionStorage || null;
  } catch {
    return null;
  }
}

/** Ruimt een oude "Beweging uit" uit localStorage op (die gold voor altijd). */
export function ruimOudeUitVoorkeur() {
  try {
    const s = getStorage();
    if (s) s.removeItem(FILM_UIT_KEY);
  } catch {
    // niets op te ruimen
  }
}

export function pushData(obj) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(obj);
  } catch {
    // meting mag nooit de pagina breken
  }
}

/** Roept cb aan na `load` + idle; geeft een functie terug die dat annuleert. */
export function naLoadEnIdle(cb) {
  let gestopt = false;
  let idleId = null;
  let timerId = null;
  const start = () => {
    if (gestopt) return;
    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(() => !gestopt && cb(), { timeout: 3000 });
    } else {
      timerId = setTimeout(() => !gestopt && cb(), IDLE_FALLBACK_MS);
    }
  };
  if (document.readyState === "complete") start();
  else window.addEventListener("load", start, { once: true });
  return () => {
    gestopt = true;
    window.removeEventListener("load", start);
    if (idleId != null && typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idleId);
    if (timerId != null) clearTimeout(timerId);
  };
}

/** Omgevingsvoorwaarden uit de browser (reduced-motion, Save-Data, 2g) samen met site en uit-schakelaar. */
export function magFilmInBrowser({ domain, storage, uitKey }) {
  const nav = typeof navigator !== "undefined" ? navigator : {};
  let uitgezet = false;
  if (uitKey) {
    try {
      // De uit-voorkeur leeft in sessionStorage; `storage` geldt voor andere sleutels.
      uitgezet = (uitKey === FILM_UIT_KEY ? getSessieStorage() : storage).getItem(uitKey) === "1";
    } catch {
      uitgezet = true;
    }
  }
  return magFilm({
    domain,
    reducedMotion: !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches),
    saveData: !!(nav.connection && nav.connection.saveData),
    effectiveType: nav.connection && nav.connection.effectiveType,
    uitgezet,
  });
}
